<template>
  <fieldset class="tw:min-w-0">
    <div class="bg-light rounded">
      <template v-if="canSetup">
        <div
          v-if="dataLocales.length > 1"
          class="border-bottom border-secondary px-3 px-md-4 py-2 py-md-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2"
        >
          <div role="tablist" :aria-label="$t('Select language')" class="tw:flex tw:flex-wrap tw:gap-2">
            <LocaleStatusBadge
              v-for="locale in dataLocales"
              :key="locale.value"
              as="button"
              type="button"
              role="tab"
              :aria-selected="currentLang === locale.value"
              :locale="locale"
              :completed="completedLocales.has(locale.value)"
              :active="currentLang === locale.value"
              :is-default="locale.value === defaultLang"
              @click="currentLang = locale.value"
            />
          </div>

          <!-- The default language is copied in one click; the others stay behind the menu. -->
          <div class="tw:ml-auto tw:inline-flex tw:items-center">
            <button
              v-if="defaultLocale && currentLang !== defaultLang"
              type="button"
              :class="copyButtonClass"
              :disabled="!completedLocales.has(defaultLang)"
              :title="completedLocales.has(defaultLang) ? null : $t('Nothing written in {language} yet', { language: defaultLocale.label })"
              @click="requestCopy(defaultLang)"
            >
              <CopyIcon class="tw:size-4" />
              {{ $t('Copy from {language}', { language: defaultLocale.label }) }}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <button type="button" :class="copyButtonClass" :aria-label="$t('Copy from another language')">
                  <template v-if="!defaultLocale || currentLang === defaultLang">
                    <CopyIcon class="tw:size-4" />
                    {{ $t('Copy from another language') }}
                  </template>
                  <ChevronDownIcon class="tw:size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" class="tw:w-56">
                <DropdownMenuLabel>{{ $t('Copy from') }}</DropdownMenuLabel>
                <DropdownMenuItem
                  v-for="locale in otherLocales"
                  :key="locale.value"
                  :disabled="!completedLocales.has(locale.value)"
                  @select="requestCopy(locale.value)"
                >
                  {{ locale.label }}
                  <span v-if="locale.value === defaultLang" class="tw:ml-auto tw:text-xs tw:text-muted-foreground">{{ $t('Default') }}</span>
                  <span v-else-if="!completedLocales.has(locale.value)" class="tw:ml-auto tw:text-xs tw:text-muted-foreground">{{ $t('Empty') }}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div ref="slotContainer" class="px-3 px-md-4 py-2 py-md-3">
          <slot :lang="currentLang" />
        </div>
      </template>

      <Alert v-else variant="danger">
        {{ $t('You must set up languages before setup custom fields') }}
      </Alert>
    </div>

    <AlertDialog v-model:open="confirmingCopy">
      <AlertDialogContent class="tw:!max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{{ $t('Replace the text in {language}?', { language: labelOf(currentLang) }) }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ $t('What is written in {language} is replaced by the text in {source}. Nothing is saved until you save the form.', { language: labelOf(currentLang), source: labelOf(copySource) }) }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel as-child>
            <Button type="button" variant="secondary">{{ $t('Cancel') }}</Button>
          </AlertDialogCancel>
          <AlertDialogAction as-child>
            <Button type="button" @click="copyFrom(copySource)">{{ $t('Replace') }}</Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </fieldset>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ChevronDownIcon, CopyIcon } from '@lucide/vue'
import Alert from '@/components/ui/Alert.vue'
import LocaleStatusBadge from '@/components/ui/LocaleStatusBadge.vue'
import { Button } from '@/shadcn/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shadcn/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/shadcn/components/ui/dropdown-menu'

const props = defineProps({
  dataLocales: {
    type: Array,
    default: () => []
  },
  dataDefaultLocale: {
    type: String,
    default: null
  },
  // The tab to open on, when it is not the default language (such as the first one with an error).
  dataOpeningLocale: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['lang-change'])

const copyButtonClass = 'tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:px-2 tw:py-1 tw:text-sm tw:text-gray-600 tw:transition-colors tw:hover:bg-gray-200 tw:hover:text-gray-900 tw:disabled:pointer-events-none tw:disabled:opacity-50'

const currentLang = ref(props.dataOpeningLocale ?? props.dataDefaultLocale)
const slotContainer = ref(null)
const completedLocales = ref(new Set())
// Kept once the confirmation closes, which happens before its button is handled.
const copySource = ref(null)
const confirmingCopy = ref(false)

const canSetup = computed(() => props.dataLocales.length > 0)

const defaultLocale = computed(() => props.dataLocales.find((l) => l.value === props.dataDefaultLocale) ?? null)
const defaultLang = computed(() => defaultLocale.value?.value ?? null)
const otherLocales = computed(() => props.dataLocales.filter((l) => l.value !== currentLang.value))

const labelOf = (lang) => props.dataLocales.find((l) => l.value === lang)?.label ?? lang

const isFieldFilled = (el) => {
  if (!el) {
    return false
  }

  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
    return (el.value ?? '').toString().trim().length > 0
  }

  const editable = el.querySelector('[contenteditable="true"]')
  if (editable) {
    return editable.textContent.trim().length > 0
  }

  const native = el.querySelector('input, textarea, select')
  if (native) {
    return (native.value ?? '').toString().trim().length > 0
  }

  const attr = el.getAttribute('value')
  if (attr !== null) {
    return attr.trim().length > 0
  }

  return false
}

const recomputeCompletion = () => {
  if (!slotContainer.value) {
    return
  }

  const next = new Set()
  slotContainer.value.querySelectorAll('[data-locale]').forEach((el) => {
    const locale = el.getAttribute('data-locale')
    if (locale && isFieldFilled(el)) {
      next.add(locale)
    }
  })

  completedLocales.value = next
}

// The control holding the value of a `data-locale` field: the field itself, or the named one
// inside it, such as the textarea a rich text editor submits.
const controlOf = (el) => (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
  ? el
  : el.querySelector('textarea[name], input[name], select[name]') ?? el.querySelector('input, textarea, select'))

// The fields of a language, keyed by their name with the language left out, so `subject[fr]`
// matches `subject[en]`. Fields without a name match by their order.
const fieldsOf = (lang) => {
  const fields = new Map()
  let unnamed = 0
  slotContainer.value?.querySelectorAll(`[data-locale="${lang}"]`).forEach((el) => {
    const control = controlOf(el)
    if (!control || control.type === 'checkbox' || control.type === 'radio') {
      return
    }
    const key = control.name ? control.name.replaceAll(`[${lang}]`, '[*]') : `#${unnamed++}`
    fields.set(key, control)
  })

  return fields
}

const copyFrom = (source) => {
  const targets = fieldsOf(currentLang.value)
  fieldsOf(source).forEach((from, key) => {
    const to = targets.get(key)
    if (!to) {
      return
    }
    to.value = from.value
    // Listeners update their state from the event, as when the field is typed in.
    to.dispatchEvent(new Event('input', { bubbles: true }))
    to.dispatchEvent(new Event('change', { bubbles: true }))
  })
  confirmingCopy.value = false
}

// Replacing what is already written asks first; an empty language is filled straight away.
const requestCopy = (source) => {
  if (completedLocales.value.has(currentLang.value)) {
    copySource.value = source
    confirmingCopy.value = true
  } else {
    copyFrom(source)
  }
}

const onFieldChange = () => recomputeCompletion()

onMounted(async () => {
  if (props.dataLocales.length > 0) {
    currentLang.value = props.dataLocales.find((l) => l.value === props.dataOpeningLocale)?.value
      ?? defaultLang.value
      ?? props.dataLocales[0].value
  }

  await nextTick()
  recomputeCompletion()

  slotContainer.value?.addEventListener('input', onFieldChange)
  slotContainer.value?.addEventListener('change', onFieldChange)
})

onBeforeUnmount(() => {
  slotContainer.value?.removeEventListener('input', onFieldChange)
  slotContainer.value?.removeEventListener('change', onFieldChange)
})

watch(currentLang, (value) => emit('lang-change', value))
</script>
